import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  FolderPlus, ArrowsClockwise, UploadSimple, Sparkle, MagicWand, Trash, Calculator, FileText, PencilSimple, PaperPlaneTilt,
  ArrowCounterClockwise, EnvelopeSimple, EnvelopeSimpleOpen, PenNib, CheckCircle, CurrencyEur, Clock, BellRinging, Note, Spinner,
  DeviceMobile, ChatCircleDots, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYP_META = {
  akte_erstellt: { icon: FolderPlus, color: "#1A2238" },
  akte_bearbeitet: { icon: PencilSimple, color: "#6B7280" },
  status_geaendert: { icon: ArrowsClockwise, color: "#0EA5E9" },
  dokument_hochgeladen: { icon: UploadSimple, color: "#1A2238" },
  dokument_analysiert: { icon: Sparkle, color: "#7C3AED" },
  daten_uebernommen: { icon: MagicWand, color: "#7C3AED" },
  dokument_geloescht: { icon: Trash, color: "#9CA3AF" },
  bezifferung_geaendert: { icon: Calculator, color: "#B45309" },
  schriftsatz_erstellt: { icon: FileText, color: "#1A2238" },
  schriftsatz_bearbeitet: { icon: PencilSimple, color: "#1A2238" },
  schriftsatz_versendet: { icon: PaperPlaneTilt, color: "#16A34A" },
  schriftsatz_versand_zurueck: { icon: ArrowCounterClockwise, color: "#9CA3AF" },
  email_eingang: { icon: EnvelopeSimpleOpen, color: "#0EA5E9" },
  email_ausgang: { icon: EnvelopeSimple, color: "#16A34A" },
  vollmacht_gesendet: { icon: PenNib, color: "#0EA5E9" },
  vollmacht_unterschrieben: { icon: PenNib, color: "#16A34A" },
  abtretung_unterschrieben: { icon: PenNib, color: "#16A34A" },
  portal_link_gesendet: { icon: DeviceMobile, color: "#0EA5E9" },
  portal_login: { icon: DeviceMobile, color: "#6B7280" },
  portal_nachricht: { icon: ChatCircleDots, color: "#D32F2F" },
  portal_antwort: { icon: ChatCircleDots, color: "#1A2238" },
  portal_upload: { icon: UploadSimple, color: "#16A34A" },
  dokument_freigegeben: { icon: Eye, color: "#0EA5E9" },
  zahlung_eingegangen: { icon: CurrencyEur, color: "#16A34A" },
  akte_abgeschlossen: { icon: CheckCircle, color: "#16A34A" },
  frist_gesetzt: { icon: Clock, color: "#D32F2F" },
  wiedervorlage: { icon: BellRinging, color: "#F59E0B" },
  notiz: { icon: Note, color: "#F59E0B" },
};
const ACTOR_LABEL = { system: "System", anwalt: "Kanzlei", ki: "KI", mandant: "Mandant", email: "E-Mail" };
const FILTER = [
  ["alle", "Alles"],
  ["dokumente", "Dokumente"],
  ["schriftsaetze", "Schriftsätze"],
  ["kommunikation", "Kommunikation"],
  ["portal", "Mandantenportal"],
  ["notizen", "Notizen"],
];
const GROUP = {
  dokumente: ["dokument_hochgeladen", "dokument_analysiert", "daten_uebernommen", "dokument_geloescht", "portal_upload", "dokument_freigegeben"],
  schriftsaetze: ["schriftsatz_erstellt", "schriftsatz_bearbeitet", "schriftsatz_versendet", "schriftsatz_versand_zurueck"],
  kommunikation: ["email_eingang", "email_ausgang", "vollmacht_gesendet", "vollmacht_unterschrieben", "abtretung_unterschrieben", "schriftsatz_versendet", "portal_nachricht", "portal_antwort", "portal_link_gesendet"],
  portal: ["portal_link_gesendet", "portal_login", "portal_nachricht", "portal_antwort", "portal_upload", "dokument_freigegeben", "vollmacht_unterschrieben", "abtretung_unterschrieben"],
  notizen: ["notiz", "wiedervorlage", "frist_gesetzt"],
};

const fmt = (iso) => {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "long", year: "numeric" }),
    time: d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
    key: d.toISOString().slice(0, 10),
  };
};

export default function AktenVerlauf({ akteId, compact = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/akten/${akteId}/verlauf`);
      setEvents(r.data);
    } catch (err) { toast.error(errMsg(err, "Verlauf konnte nicht geladen werden")); } finally { setLoading(false); }
  };
  useEffect(() => { if (akteId) load(); /* eslint-disable-next-line */ }, [akteId]);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/akten/${akteId}/verlauf`, { beschreibung: note.trim() });
      setNote("");
      toast.success("Notiz gespeichert");
      load();
    } catch (err) { toast.error(errMsg(err, "Notiz konnte nicht gespeichert werden")); } finally { setSaving(false); }
  };

  const visible = filter === "alle" ? events : events.filter(e => GROUP[filter]?.includes(e.typ));
  const groups = [];
  visible.forEach(e => {
    const f = fmt(e.created_at);
    const g = groups[groups.length - 1];
    if (g && g.key === f.key) g.items.push({ ...e, _t: f.time }); else groups.push({ key: f.key, day: f.day, items: [{ ...e, _t: f.time }] });
  });

  return (
    <div className="space-y-5" data-testid="akten-verlauf">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-2" data-testid="verlauf-note-box">
        <p className="text-[11px] uppercase tracking-wide text-[#9CA3AF]">Notiz zum Verlauf</p>
        <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="z. B. Telefonat mit Versicherung: Sachbearbeiterin sagt Zahlung bis 15.07. zu" data-testid="verlauf-note-input" />
        <div className="flex justify-end">
          <Button size="sm" onClick={addNote} disabled={saving || !note.trim()} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="verlauf-note-save">
            {saving ? <Spinner size={12} className="mr-1.5 animate-spin" /> : <Note size={12} className="mr-1.5" />} Notiz speichern
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" role="tablist">
        {FILTER.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`verlauf-filter-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filter === k ? "bg-[#1A2238] text-white border-[#1A2238]" : "border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]"}`}>
            {l}
          </button>
        ))}
        <span className="ml-auto text-xs text-[#9CA3AF] self-center" data-testid="verlauf-count">{visible.length} Einträge</span>
      </div>

      {loading ? (
        <p className="text-sm text-[#6B7280]">Lädt Verlauf …</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-[#6B7280]" data-testid="verlauf-empty">Noch keine Einträge.</p>
      ) : (
        <div className="space-y-6">
          {groups.map(g => (
            <div key={g.key}>
              <p className="text-xs font-semibold text-[#6B7280] mb-3 sticky top-0 bg-[#FAFAFA] py-1" data-testid={`verlauf-day-${g.key}`}>{g.day}</p>
              <ol className="relative border-l-2 border-[#E5E7EB] ml-3 space-y-4">
                {g.items.map(e => {
                  const meta = TYP_META[e.typ] || { icon: Note, color: "#6B7280" };
                  const Icon = meta.icon;
                  return (
                    <li key={e.id} className="relative pl-7" data-testid={`verlauf-item-${e.typ}`}>
                      <span className="absolute -left-[13px] top-0.5 h-6 w-6 rounded-full border-2 border-white shadow-sm flex items-center justify-center" style={{ background: meta.color }}>
                        <Icon size={12} weight="bold" className="text-white" />
                      </span>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#0A192F]">{e.titel}</span>
                        <span className="text-[11px] font-mono text-[#9CA3AF]">{e._t}</span>
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]">{ACTOR_LABEL[e.actor] || e.actor}</span>
                      </div>
                      {e.beschreibung && <p className={`text-sm text-[#4B5563] mt-0.5 ${compact ? "line-clamp-2" : "whitespace-pre-wrap"}`}>{e.beschreibung}</p>}
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
